import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';
import { http } from '@/api/http';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import SnippetsOutlined from '@ant-design/icons/SnippetsOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import CloseCircleOutlined from '@ant-design/icons/CloseCircleOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',
  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION (COLABORADOR) ||============================== //

export default function NotificationCollaborator() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [badgeCount, setBadgeCount] = useState(0);
  const [notifications, setNotifications] = useState([]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const { data } = await http.get('/order-edit-requests/my-notifications');

      const items = Array.isArray(data.data) ? data.data : [];

      const mapped = items.map((item) => ({
        id: item.id,
        status: item.status, // approved | rejected
        order_id: item.order_id,
        title: `Orden ${item.order?.order_number ?? `#${item.order_id}`}`,
        message:
          item.status === 'approved'
            ? 'Tu solicitud para editar esta orden fue APROBADA. Ya puedes entrar a editar.'
            : 'Tu solicitud para editar esta orden fue RECHAZADA.',
        time_label: new Date(item.created_at).toLocaleString(),
        raw: item
      }));

      setNotifications(mapped);
      setBadgeCount(data.total ?? mapped.length);
    } catch (error) {
      console.error('Error loading collaborator notifications', error);
    } finally {
      setLoading(false);
    }
  };

  // Carga la data al montar
  useEffect(() => {
    fetchNotifications();
  }, []);

  // Y cada vez que se abre la campana
  useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open]);

  const handleMarkAllAsRead = () => {
    // Por ahora, solo limpiamos el badge en frontend
    setBadgeCount(0);
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open notifications"
        ref={anchorRef}
        aria-controls={open ? 'notification-collaborator' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={badgeCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>

      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper
              sx={(theme) => ({
                boxShadow: theme.customShadows.z1,
                width: '100%',
                minWidth: 420,
                maxWidth: { xs: 420, md: 420 }
              })}
            >
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notificaciones"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {badgeCount > 0 && (
                        <Tooltip title="Marcar todo como leído">
                          <IconButton color="success" size="small" onClick={handleMarkAllAsRead}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                    {loading && (
                      <ListItem>
                        <ListItemText primary={<Typography variant="body2">Cargando notificaciones...</Typography>} />
                      </ListItem>
                    )}

                    {!loading && notifications.length === 0 && (
                      <ListItem>
                        <ListItemText primary={<Typography variant="body2">No tienes notificaciones aún.</Typography>} />
                      </ListItem>
                    )}

                    {!loading &&
                      notifications.map((notif) => (
                        <ListItem
                          key={notif.id}
                          component={ListItemButton}
                          divider
                          selected={notif.status === 'approved'}
                          onClick={() => {
                            setOpen(false);
                            navigate(`/orders/${notif.order_id}/edit`);
                          }}
                          secondaryAction={
                            <Typography variant="caption" noWrap>
                              {notif.time_label || ''}
                            </Typography>
                          }
                        >
                          <ListItemAvatar>
                            <Avatar
                              sx={{
                                color: notif.status === 'approved' ? 'success.main' : 'error.main',
                                bgcolor: notif.status === 'approved' ? 'success.lighter' : 'error.lighter'
                              }}
                            >
                              {notif.status === 'approved' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Typography variant="h6" noWrap>
                                {notif.title}
                              </Typography>
                            }
                            secondary={notif.message}
                          />
                        </ListItem>
                      ))}

                    <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }} onClick={() => {
    setOpen(false);
    navigate('/order-edit-requests-list');
  }}>
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            Ver todas
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
